if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupPersonalisationCounter();
        this.setupPersonalisationFontPreview();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const personalisationAddOnVariantId = formData.get('properties[_personalisation_addon_variant_id]');
        const customSaying = String(formData.get('properties[Custom saying]') || '').trim();
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status || !personalisationAddOnVariantId || !customSaying) {
              return response;
            }

            return this.addPersonalisationFeeItem(response, personalisationAddOnVariantId, formData);
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      setupPersonalisationCounter() {
        const personalisationInputs = this.form.querySelectorAll('[data-personalisation-maxlength]');

        personalisationInputs.forEach((personalisationInput) => {
          const personalisationRemaining = personalisationInput
            .closest('.product-form__input')
            ?.querySelector('[data-personalisation-remaining]');

          if (!personalisationRemaining) return;

          const maxLength = Number(
            personalisationInput.dataset.personalisationMaxlength || personalisationInput.maxLength
          );
          const updateRemaining = () => {
            personalisationRemaining.textContent = Math.max(maxLength - personalisationInput.value.length, 0);
          };

          updateRemaining();
          personalisationInput.addEventListener('input', updateRemaining);
        });
      }

      setupPersonalisationFontPreview() {
        const personalisationInput = this.form.querySelector('.product-personalisation__input');
        const fontSelect = this.form.querySelector('[data-personalisation-font-select]');

        if (!personalisationInput || !fontSelect) return;

        const updatePreviewFont = () => {
          const selectedOption = fontSelect.options[fontSelect.selectedIndex];
          if (!selectedOption?.dataset.fontFamily) return;

          personalisationInput.style.fontFamily = selectedOption.dataset.fontFamily;
        };

        updatePreviewFont();
        fontSelect.addEventListener('change', updatePreviewFont);
      }

      addPersonalisationFeeItem(mainItemResponse, personalisationAddOnVariantId, formData) {
        const addOnFormData = new FormData();
        addOnFormData.append('id', personalisationAddOnVariantId);
        addOnFormData.append('quantity', formData.get('quantity') || '1');
        addOnFormData.append('properties[_personalisation_fee_item]', 'true');
        addOnFormData.append('properties[_personalisation_parent_key]', mainItemResponse.key);
        addOnFormData.append('properties[Custom saying]', formData.get('properties[Custom saying]'));

        if (this.cart) {
          addOnFormData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          addOnFormData.append('sections_url', window.location.pathname);
        }

        const addOnConfig = fetchConfig('javascript');
        addOnConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete addOnConfig.headers['Content-Type'];
        addOnConfig.body = addOnFormData;

        return fetch(`${routes.cart_add_url}`, addOnConfig).then((response) => response.json());
      }
    }
  );
}
